import Resource from 'Resources/ResourceTyped';
import type PublicChatbotConversationModel from './Model/PublicChatbotConversation';
import type { PublicChatbotMessage } from './Model/PublicChatbotConversation';
import type { SidebarCountFiltersBuilt, SidebarCountResult } from 'App/components/ListViewSidebar/Context/Helpers';

/**
 * The public chatbot's conversations (WEB-11291): read by Admin › Public
 * chatbot › Conversations, the to-do and ticket cards and the card timelines
 * (WEB-12989). The API's `supportChatbotGuard.ts` holds who may read what.
 *
 * ## `/public-chatbot-conversations`, not `/public-chatbots/...`
 *
 * `Resources/PublicChatbot` owns that other namespace and `routing.test.ts`
 * asserts no one else claims it. The split is not cosmetic: those endpoints are
 * admin-only and sit behind the AI Workspace's per-user opt-in, so a support
 * agent or a sales rep reaches none of them. These carry their own gate.
 *
 * `find` is inherited and hits `PathType.FIND`, driven by a RequestBuilder
 * query. Only what the generic handlers cannot do is declared here.
 */
/**
 * Unwrap the `{ error, data, metadata }` envelope.
 *
 * `_getRequest` and friends resolve to the axios RESPONSE, so `response.data` is
 * the HTTP body — and every handler on this controller returns the documented
 * envelope, so the payload is one level further in. Reading `response.data`
 * directly hands the caller the envelope object: `messages.map is not a
 * function`, a blank detail page, and a 200 in every log.
 */
const payload = <T>(response: { data?: { data?: T } }): T | undefined => response?.data?.data;

class PublicChatbotConversation extends Resource<PublicChatbotConversationModel> {
	eventName = 'publicChatbotConversation';

	constructor() {
		super('public-chatbot-conversations');
	}

	/** Row counts for the sidebar's saved views. Required by `ListViewSidebar`. */
	async getListViewCounts(filters: SidebarCountFiltersBuilt[]): Promise<SidebarCountResult[]> {
		const response = await this._postRequest('listCounts', { filters }, { methodName: 'getListViewCounts' });
		return payload<SidebarCountResult[]>(response) ?? [];
	}

	/**
	 * The handed-over chats of one company or contact, newest first, for its
	 * card's timeline (WEB-12989). Only those the reader may open.
	 */
	async getLinked(link: { clientId?: number; contactId?: number }): Promise<PublicChatbotConversationModel[]> {
		const response = await this._getRequest('linked', { methodName: 'getLinked', params: link });
		return payload<PublicChatbotConversationModel[]>(response) ?? [];
	}

	/**
	 * Sales › Website chats (WEB-13681): one page of the chats this user may
	 * read, in the order asked for. Who sees which chat is the API's rule.
	 */
	async findWebsiteChats(
		params: WebsiteChatsParams
	): Promise<{ data: PublicChatbotConversationModel[]; total: number }> {
		const response = await this._getRequest('website-chats', { methodName: 'findWebsiteChats', params });
		const body = response?.data as { data?: PublicChatbotConversationModel[]; metadata?: { total?: number } };
		return { data: body?.data ?? [], total: body?.metadata?.total ?? 0 };
	}

	/** Whether the account runs a public chatbot — when Sales › Website chats is in the nav. */
	async websiteChatsAvailable(): Promise<boolean> {
		const response = await this._getRequest('website-chats/available', { methodName: 'websiteChatsAvailable' });
		return !!payload<{ available: boolean }>(response)?.available;
	}

	/** One conversation's turns, oldest first. */
	async getMessages(id: number): Promise<PublicChatbotMessage[]> {
		const response = await this._getRequest(`${id}/messages`, { methodName: 'getMessages' });
		return payload<PublicChatbotMessage[]>(response) ?? [];
	}

	/**
	 * Where a visitor's file is opened (WEB-12351): a plain link the browser
	 * follows with the user's own session, so an image thumbnail is an `<img>`
	 * and a PDF opens in a tab.
	 */
	attachmentUrl(conversationId: number, messageId: number, index: number): string {
		return this._getUrl(`${conversationId}/messages/${messageId}/attachments/${index}`);
	}

	/**
	 * GDPR-erase the person this conversation belongs to (WEB-11814): all their
	 * conversations, sessions and identity, and the linked CRM contact unless
	 * `eraseCrmContact` is false. Admin-only on the server.
	 */
	async erasePerson(id: number, eraseCrmContact: boolean): Promise<PublicChatbotErasureReport> {
		const response = await this._postRequest(
			`${id}/erase`,
			{ erase_crm_contact: eraseCrmContact },
			{ methodName: 'erasePerson' }
		);
		return payload<PublicChatbotErasureReport>(response)!;
	}
}

/**
 * The list route's query (WEB-13681): `q` filters on `handOver`, `assignee`,
 * `client.id` and `lastActivity`, a free-text `search`, and `sort` as `-field`.
 */
export type WebsiteChatsParams = {
	q: Array<{ a?: string; c?: string; v?: unknown }>;
	search?: string;
	sort?: string;
	offset: number;
	limit: number;
};

export type PublicChatbotErasureReport = {
	contactsErased: number;
	conversations: number;
	messages: number;
	crmContactErased: number[];
};

const resource = new PublicChatbotConversation();

export default resource;
